import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, UserSearch, AlertTriangle, Sparkles, Merge, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FeatureGate } from "@/components/feature-gate";
import { listGlobalPeople, explainChurnRisk, mergeGlobalContacts } from "@/lib/command-center.functions";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";

export const Route = createFileRoute("/_authenticated/people")({
  component: PeoplePage,
});

function PeoplePage() {
  return (
    <FeatureGate feature="global_people" showBlurred={false}>
      <PeopleInner />
    </FeatureGate>
  );
}

function PeopleInner() {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"ltv" | "recent" | "name">("ltv");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [explaining, setExplaining] = useState<string | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const limit = 50;

  const qc = useQueryClient();
  const fetchPeople = useServerFn(listGlobalPeople);
  const explain = useServerFn(explainChurnRisk);
  const merge = useServerFn(mergeGlobalContacts);

  const { data, isLoading } = useQuery({
    queryKey: ["global-people", search, sort, page],
    queryFn: () => fetchPeople({ data: { search, sort, limit, offset: page * limit } }),
  });

  const handleExplain = async (id: string) => {
    setExplaining(id);
    try {
      const res = await explain({ data: { global_contact_id: id } });
      toast.success(`Risk: ${(res.score * 100).toFixed(0)}%, ${res.explanation}`, { duration: 8000 });
      qc.invalidateQueries({ queryKey: ["global-people"] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setExplaining(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : s.length >= 2 ? [s[1], id] : [...s, id]);
  };

  const handleMerge = async (keepId: string) => {
    const mergeId = selected.find((s) => s !== keepId);
    if (!mergeId) return;
    try {
      await merge({ data: { keep_id: keepId, merge_id: mergeId } });
      toast.success("Contacts merged");
      setSelected([]);
      setMergeOpen(false);
      qc.invalidateQueries({ queryKey: ["global-people"] });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const people = data?.people ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <UserSearch className="h-7 w-7 text-primary" /> Global People
          </h1>
          <p className="text-muted-foreground mt-1">
            Customers deduplicated across every program. Your real LTV, not per-program guesses.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Total unique people</div>
          <div className="text-2xl font-bold tabular-nums">{total.toLocaleString()}</div>
        </div>
      </div>

      {selected.length === 2 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="text-sm">
              2 contacts selected, merge into one global profile?
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelected([])}>Cancel</Button>
              <Button size="sm" onClick={() => setMergeOpen(true)}>
                <Merge className="h-3.5 w-3.5 mr-1" /> Merge
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                placeholder="Search by email or name..."
                className="pl-9"
              />
            </div>
            <Select value={sort} onValueChange={(v) => setSort(v as any)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ltv">Highest LTV</SelectItem>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="name">Name (A–Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Programs</TableHead>
                <TableHead className="text-right">Total LTV</TableHead>
                <TableHead>Last Activity</TableHead>
                <TableHead>Churn Risk</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && people.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-0">
                    <EmptyState
                      icon={UserSearch}
                      title="Your global people index appears here"
                      description="Contacts roll up automatically after your first CSV import."
                    />
                  </TableCell>
                </TableRow>
              )}
              {people.map((p: any) => (
                <TableRow key={p.id} className={selected.includes(p.id) ? "bg-primary/5" : ""}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selected.includes(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="h-4 w-4"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{p.merged_name || p.primary_email}</div>
                    <div className="text-xs text-muted-foreground">{p.primary_email}</div>
                    {p.all_emails?.length > 1 && (
                      <div className="text-xs text-muted-foreground">+{p.all_emails.length - 1} alias</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.programs ?? []).slice(0, 4).map((pg: any) => (
                        <Badge key={pg.workspace_id} variant="secondary" className="text-xs">
                          {pg.program_name}
                        </Badge>
                      ))}
                      {(p.programs ?? []).length > 4 && (
                        <Badge variant="outline" className="text-xs">+{p.programs.length - 4}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    ${Number(p.total_ltv ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.last_activity_at ? new Date(p.last_activity_at).toLocaleDateString() : ","}
                  </TableCell>
                  <TableCell>
                    {p.churn_risk_score != null ? (
                      <div className="flex items-center gap-1.5">
                        {p.churn_risk_score >= 0.7 && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
                        <span
                          className={
                            p.churn_risk_score >= 0.7
                              ? "text-destructive font-medium"
                              : p.churn_risk_score >= 0.4
                              ? "text-amber-500"
                              : "text-muted-foreground"
                          }
                        >
                          {(p.churn_risk_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">,</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExplain(p.id)}
                      disabled={explaining === p.id}
                    >
                      {explaining === p.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                      <span className="ml-1 text-xs">Explain</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > limit && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total.toLocaleString()}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * limit >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pick the profile to keep</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The other will be merged into it. All emails, programs, and LTV will combine. This can't be undone, but the merge is logged.
          </p>
          <div className="space-y-2">
            {selected.map((id) => {
              const p = (data?.people ?? []).find((x: any) => x.id === id);
              if (!p) return null;
              return (
                <Button
                  key={id}
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  onClick={() => handleMerge(id)}
                >
                  <div className="text-left">
                    <div className="font-medium">{p.merged_name || p.primary_email}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.primary_email} · ${Number(p.total_ltv).toFixed(2)} LTV
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMergeOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
